/* eslint-disable jsx-a11y/iframe-has-title */
import ReactPDF from '@intelllex/react-pdf';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  SvgIcon,
  makeStyles,
  Typography
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useState } from 'react';
import type { FC } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { legalDocumentApi } from 'src/api/legalDocumentApi';
import DownloadIcon from 'src/icons/Download';
import TimeIcon from 'src/icons/X';
import useAuth from 'src/hooks/useAuth';
import logger from 'src/utils/logger';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiDialog-paper': {
      height: '100%',
      borderRadius: 4,
    },
    '& .MuiPaper-root': {
      overflow: 'unset !important'
    },
    '& .MuiDialogContent-root': {
      boxShadow: '0px 2px 7px rgba(0, 0, 0, 0.25) !important'
    }
  },
  deleteBtn: {
    color: '#AB3535',
    '&:hover': {
      background: 'transparent'
    }
  },
  cancelBtn: {
    color: '#6b778c',
    '&:hover': {
      background: 'transparent'
    }
  },
  downloadButton: {
    borderRadius: 4,
    textTransform: 'uppercase',
    marginTop: 8,
    marginRight: 32,
    [theme.breakpoints.down('md')]: {
      marginBottom: 15,
      width: '100%'
    }
  },
  downloadButtonWrapper: {
    position: 'absolute',
    right: 56,
    top: 8,
  },
  loadingCircle: {
    color: '#006699',
    width: '20px !important',
    height: '20px !important',
    marginLeft: 5,
    marginRight: 5
  },
}));

interface LegalDocumentModalProps {
  openLegalDocumentModal: any;
  closeLegalDocumentModal: any;
  selectedLegalDocument: any;
}

const LegalDocumentModal: FC<LegalDocumentModalProps> = (props) => {
  const classes = useStyles();
  const { openLegalDocumentModal, closeLegalDocumentModal, selectedLegalDocument } = props;
  const locale = localStorage.getItem('locale');
  const { t } = useTranslation(locale);
  const { isAuthenticated } = useAuth();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  // Documents are uploaded per language: `url` holds the English file and the other
  // locales live on `url_<locale>`. Fall back to the English file whenever the current
  // locale has no translated document uploaded.
  const getLocalizedDocumentUrl = () => {
    if (!locale || locale === 'en') {
      return selectedLegalDocument?.url;
    }
    return selectedLegalDocument?.[`url_${locale}`] || selectedLegalDocument?.url;
  };

  const getDownloadFileName = () => {
    const documentUrl = getLocalizedDocumentUrl();
    const documentName = selectedLegalDocument?.name[locale];
    const extension = documentUrl?.split('.')?.pop();
    if (documentName?.length > 0 && extension?.length > 0) {
      return `${documentName}.${extension}`;
    }
    return documentUrl;
  };

  const handleDownloadLegalDocument = async () => {
    setIsDownloading(true);
    try {
      const res: any = await legalDocumentApi.downloadLegalDocument(getLocalizedDocumentUrl());
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getDownloadFileName());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setIsDownloading(false);
    } catch (err) {
      logger(err, 'error');
      toast.error(t('legal_document_modal_download_error'));
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={openLegalDocumentModal}
      onClose={closeLegalDocumentModal}
      fullScreen
      maxWidth="xl"
      sx={{
        height: '100%',
      }}
      fullWidth
      className={classes.root}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          <Grid
            item
            xs={12}
            sm={9}
          >
            <Typography
              color="textPrimary"
              variant="h5"
            >
              { selectedLegalDocument?.name[locale] }
            </Typography>
            {!isAuthenticated && (
              <Typography
                color="textPrimary"
                variant="h6"
              >
                { selectedLegalDocument?.title[locale] }
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            sm={3}
          >
            <Box className={classes.downloadButtonWrapper}>
              <Button
                className={classes.downloadButton}
                color="primary"
                disabled={isDownloading}
                onClick={handleDownloadLegalDocument}
                size="small"
                variant="contained"
                id="legal_document_download_button"
                startIcon={(
                  <SvgIcon fontSize="small">
                    <DownloadIcon />
                  </SvgIcon>
                )}
              >
                {t('legal_document_modal_download_button')}
                {isDownloading && (
                  <CircularProgress className={classes.loadingCircle} />
                )}
              </Button>
            </Box>
            <IconButton
              aria-label="close"
              onClick={closeLegalDocumentModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <TimeIcon />
            </IconButton>
          </Grid>
        </Box>
      </DialogTitle>
      <DialogContent>
        <ReactPDF
          url={`${process.env.REACT_APP_IMAGE_UPLOAD_URL}/images/user/${getLocalizedDocumentUrl()}?v1.2`}
          showProgressBar
          showToolbox
        />
      </DialogContent>
    </Dialog>
  );
};

LegalDocumentModal.propTypes = {
  openLegalDocumentModal: PropTypes.bool,
  closeLegalDocumentModal: PropTypes.func,
};

export default LegalDocumentModal;
